import { pendingTakeawayAPI } from 'api/pendingTakeaway'
import type { PendingTakeawayOfflineSyncOp } from 'api/pendingTakeaway'
import {
  getPendingTakeawayJournalOrder,
  listPendingTakeawayJournalOrders,
  mapJournalActionToSyncAction,
  markPendingTakeawayJournalOps,
  recoverHangingKitchenIntent,
  unsyncedJournalOps,
  type PendingTakeawayJournalOp,
  type PendingTakeawayJournalOrder,
} from 'store/pendingTakeawayJournal'
import { resolveOfflineScope } from 'store/posStore'

const orderLocks = new Map<string, Promise<unknown>>()

async function withFlushLock<T>(clientUuid: string, fn: () => Promise<T>): Promise<T> {
  const prior = orderLocks.get(clientUuid) ?? Promise.resolve()
  let release: () => void = () => undefined
  const gate = new Promise<void>((resolve) => {
    release = resolve
  })
  const chained = prior.then(() => gate)
  orderLocks.set(clientUuid, chained)
  await prior.catch(() => undefined)
  try {
    return await fn()
  } finally {
    release()
    if (orderLocks.get(clientUuid) === chained) orderLocks.delete(clientUuid)
  }
}

function kitchenAckPayload(op: PendingTakeawayJournalOp): Record<string, unknown> {
  const payload = { ...op.payload }
  payload.outcome = op.action === 'KITCHEN_PRINT_UNCERTAIN' ? 'uncertain' : (payload.outcome ?? 'ack')
  return payload
}

function toSyncOps(ops: PendingTakeawayJournalOp[]): PendingTakeawayOfflineSyncOp[] {
  const mapped: PendingTakeawayOfflineSyncOp[] = []
  for (const op of ops) {
    const action = mapJournalActionToSyncAction(op.action)
    if (!action) continue
    mapped.push({
      seq: op.seq,
      client_op_id: op.clientOpId,
      action,
      payload: action === 'kitchen_ack' ? kitchenAckPayload(op) : { ...op.payload },
    })
  }
  return mapped
}

export async function flushPendingTakeawayJournalOrder(
  clientUuid: string,
): Promise<{ ok: boolean; errors: string[]; conflict: boolean }> {
  return withFlushLock(clientUuid, async () => {
    await recoverHangingKitchenIntent(clientUuid)
    const order = await getPendingTakeawayJournalOrder(clientUuid)
    if (!order) return { ok: true, errors: [], conflict: false }
    const pending = unsyncedJournalOps(order)
    if (pending.length === 0) return { ok: true, errors: [], conflict: false }

    await markPendingTakeawayJournalOps({
      clientUuid,
      clientOpIds: pending.map((op) => op.clientOpId),
      syncStatus: 'syncing',
      scope: { branchId: order.branchId, tenantId: order.tenantId },
    })

    try {
      const result = await pendingTakeawayAPI.offlineSync({
        client_uuid: order.clientUuid,
        branch_id: order.branchId,
        shift_id: order.shiftId,
        pos_register_id: order.posRegisterId,
        register_session_id: order.registerSessionId,
        operations: toSyncOps(pending),
      })
      return await applySyncResult(order, pending, result)
    } catch (error) {
      const parsed = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response
      const data = parsed?.data
      if (data && Array.isArray(data.operations)) {
        return applySyncResult(order, pending, data as Parameters<typeof applySyncResult>[2])
      }
      await markPendingTakeawayJournalOps({
        clientUuid,
        clientOpIds: pending.map((op) => op.clientOpId),
        syncStatus: 'failed',
        error: error instanceof Error ? error.message : 'offline_takeaway_sync_failed',
        scope: { branchId: order.branchId, tenantId: order.tenantId },
      })
      return { ok: false, errors: [error instanceof Error ? error.message : 'offline_takeaway_sync_failed'], conflict: false }
    }
  })
}

async function applySyncResult(
  order: PendingTakeawayJournalOrder,
  pending: PendingTakeawayJournalOp[],
  result: {
    status?: string
    code?: string
    message?: string
    sale_id?: number | null
    operations?: Array<{
      client_op_id?: string | null
      status?: string
      code?: string
      message?: string
      sale_id?: number
      cart_revision?: number
    }>
    data?: { id?: number; cart_revision?: number | null }
  },
): Promise<{ ok: boolean; errors: string[]; conflict: boolean }> {
  const errors: string[] = []
  let conflict = false
  const saleId = Number(result.sale_id ?? result.data?.id ?? order.saleId ?? 0) || order.saleId
  const rows = Array.isArray(result.operations) ? result.operations : []
  const byOpId = new Map(rows.map((row) => [String(row.client_op_id ?? ''), row]))

  for (const op of pending) {
    const row = byOpId.get(op.clientOpId)
    const status = String(row?.status ?? (result.status === 'success' ? 'synced' : 'failed'))
    if (status === 'synced') {
      await markPendingTakeawayJournalOps({
        clientUuid: order.clientUuid,
        clientOpIds: [op.clientOpId],
        syncStatus: 'synced',
        saleId,
        cartRevision: row?.cart_revision ?? result.data?.cart_revision ?? null,
        scope: { branchId: order.branchId, tenantId: order.tenantId },
      })
      continue
    }
    if (status === 'skipped') {
      continue
    }
    const isConflict = status === 'conflict' || result.status === 'conflict'
    conflict = conflict || isConflict
    const message = row?.message || result.message || 'offline_takeaway_sync_failed'
    errors.push(message)
    await markPendingTakeawayJournalOps({
      clientUuid: order.clientUuid,
      clientOpIds: [op.clientOpId],
      syncStatus: isConflict ? 'conflict' : 'failed',
      saleId,
      error: message,
      conflictCode: row?.code || result.code || null,
      localStatus: isConflict ? 'CONFLICT_NEEDS_REVIEW' : undefined,
      scope: { branchId: order.branchId, tenantId: order.tenantId },
    })
  }

  return { ok: errors.length === 0 && !conflict, errors, conflict }
}

export async function flushPendingTakeawayJournal(): Promise<{ flushed: number; errors: string[]; conflicts: number }> {
  const scope = resolveOfflineScope()
  const orders = await listPendingTakeawayJournalOrders(scope)
  let flushed = 0
  let conflicts = 0
  const errors: string[] = []
  for (const order of orders) {
    if (order.origin === 'server' && !order.ops.some((op) => op.action === 'CREATE_PENDING')) {
      continue
    }
    const result = await flushPendingTakeawayJournalOrder(order.clientUuid)
    if (result.ok) flushed += 1
    if (result.conflict) conflicts += 1
    errors.push(...result.errors)
  }
  return { flushed, errors, conflicts }
}
